import { useLoaderData, useNavigate } from "react-router-dom";
import BandDeco from "./BandDeco";
import { Profile } from "../../services/profile/profileModel";
import { Fragment, useMemo, useState } from "react";
import Dialog from "../../components/Dialog";
import { createProfileRequest } from "../../services/profile/profileApi";
import CandidateProfileCard from "./profile/CandidateProfileCard";
import CandidateIcon from "./CandidateIcon";
import { compareBoolean, orderByField } from "../../react-helpers/array";
import { User } from "../../services/user/userModel";
import { Trans, useTranslation } from "react-i18next";
import ProfileForm from "./profile/ProfileForm";
import { userService } from "../../services/user/userService";
import { loggerBuilder } from "../../services/logger";
import { toastsWithIntl } from "../../services/toastService";
import { cx } from "../../react-helpers/css";

const AddProfileBtn = ({
  onClick,
  label,
  hint,
  onBand,
}: {
  onClick: () => void;
  label: string;
  hint?: string;
  onBand?: boolean;
}) => (
  <button className={cx(["cand-add", onBand && "--on-band"])} onClick={onClick}>
    <span className="cand-add_plus">
      <CandidateIcon name="plus" />
    </span>
    {label}
    {hint && <span className="cand-add_hint">{hint}</span>}
  </button>
);

const CandidateDashboardPage = () => {
  const { t } = useTranslation();
  const { profiles, loggedUser } = useLoaderData() as {
    profiles: Profile[];
    loggedUser: User;
  };
  const navigate = useNavigate();
  const { getPremiumUserLink } = userService();
  const { toastError } = toastsWithIntl(["common"]);
  const logger = loggerBuilder("CandidateDashboardPage");

  const [showCreateProfile, setShowCreateProfile] = useState(false);
  const [loadingPremium, setLoadingPremium] = useState(false);

  const sortedProfiles = useMemo(
    () => profiles.sort(orderByField("featured", true, compareBoolean)),
    [profiles],
  );

  const profilesSlots = useMemo(
    () => [...Array(4)].map((_, idx) => sortedProfiles[idx] ?? null),
    [sortedProfiles],
  );

  const hasSubscription = useMemo(() => {
    return (
      loggedUser.subscriptionEndDate &&
      new Date(loggedUser.subscriptionEndDate) > new Date() &&
      loggedUser.subscriptionStartDate !== null
    );
  }, [loggedUser]);

  return (
    <div className="layout_content cand-page">
      <section className="ui-band cand-band">
        <BandDeco />
        <div className="ui-band_in cand-main">
          <div>
            <p className="ui-band_sub cand-greet">
              {t("candidate:dashboard.GREETING", {
                name: loggedUser.firstname,
              })}{" "}
              <span aria-hidden="true">👋</span>
            </p>
            {profilesSlots[0] && (
              <>
                <h1 id="cand-main-title">
                  {t("candidate:dashboard.MAIN_PROFILE")}
                </h1>
                <p className="ui-band_sub">
                  {t("candidate:dashboard.CREATED_AT", {
                    date: new Date(profilesSlots[0].createdAt),
                  })}
                </p>
              </>
            )}
            <div className="ui-card --pad cand-tips">
              <p className="ui-small cand-tips_h">
                {t("record:config.TIPS_DETAILS")}
              </p>
              <ul className="ui-small cand-tips_list">
                <li>{t("record:config.TIPS_TRY_AGAIN")}</li>
                <li>{t("record:config.TIPS_VIDEO_PRIVACY")}</li>
                <li>{t("record:config.TIPS_DISABLE_ANYTIME")}</li>
              </ul>
              <p className="ui-small ui-muted">
                <Trans
                  i18nKey="candidate:dashboard.TIPS_ON_SITE"
                  components={{
                    strong: (
                      <a
                        href={t("candidate:CANDIDATE_GUIDE_LINK")}
                        target="_blank"
                        rel="noreferrer"
                      />
                    ),
                  }}
                />
              </p>
            </div>
          </div>
          <div className="cand-main_card">
            {profilesSlots[0] ? (
              <CandidateProfileCard profile={profilesSlots[0]} />
            ) : (
              <AddProfileBtn
                onBand
                onClick={() => setShowCreateProfile(true)}
                label={t("candidate:dashboard.CREATE_PROFILE_BUTTON")}
              />
            )}
          </div>
        </div>
      </section>

      {hasSubscription && (
        <section className="cand-prem" aria-labelledby="cand-prem-title">
          <div className="cand-prem_in">
            <h2 id="cand-prem-title">
              {t("candidate:dashboard.premium.TITLE")}{" "}
              <span className="cand-prem_z" aria-hidden="true">
                ⚡
              </span>
            </h2>
            <p>{t("candidate:dashboard.premium.DESCRIPTION")}</p>
            <div className="ui-grid --g3 cand-prem_grid">
              {profilesSlots.slice(1).map((profile, idx) => (
                <Fragment
                  key={profile ? `profile-${profile.id}` : `add-profile-${idx}`}
                >
                  {profile ? (
                    <CandidateProfileCard profile={profile} />
                  ) : (
                    <AddProfileBtn
                      onClick={() => setShowCreateProfile(true)}
                      label={t("candidate:dashboard.ADD_PROFILE")}
                      hint={t("candidate:dashboard.SLOT", { n: idx + 2 })}
                    />
                  )}
                </Fragment>
              ))}
            </div>
          </div>
        </section>
      )}

      {!hasSubscription && (
        <section className="cand-prem" aria-labelledby="cand-prem-title">
          <div className="cand-prem_in cand-prem_teaser">
            <div>
              <h2 id="cand-prem-title">
                {t("candidate:dashboard.premium.test.TITLE")}{" "}
                <span className="cand-prem_z" aria-hidden="true">
                  ⚡
                </span>
              </h2>
              <p
                dangerouslySetInnerHTML={{
                  __html: t("candidate:dashboard.premium.test.DESCRIPTION"),
                }}
              ></p>
              <div className="cand-prem_actions">
                <a
                  className="btn--white"
                  href="https://www.2minutesmax.com/premium-candidats"
                  target="_blank"
                  rel="noreferrer"
                >
                  {t("candidate:dashboard.premium.test.BUTTON_MORE")}
                </a>
                <button
                  className="btn--1"
                  disabled={loadingPremium}
                  onClick={() => {
                    const handlePremiumClick = async () => {
                      try {
                        setLoadingPremium(true);
                        const result = await getPremiumUserLink(loggedUser.id);
                        if (result?.url) window.location.href = result.url;
                      } catch (err) {
                        logger.error(
                          "Erreur lors de la récupération du lien premium",
                          err,
                        );
                        toastError("common:error.header.PREMIUM_LINK_ERROR");
                        return;
                      } finally {
                        setLoadingPremium(false);
                      }
                    };

                    void handlePremiumClick();
                  }}
                >
                  {loadingPremium ? (
                    "..."
                  ) : (
                    <>
                      <CandidateIcon name="bolt" />
                      {t("candidate:dashboard.premium.test.BUTTON_TRY")}
                    </>
                  )}
                </button>
              </div>
            </div>
            <div aria-hidden="true">
              <div className="cand-slots">
                <i className="--on">
                  <CandidateIcon name="film" />
                </i>
                <i>
                  <CandidateIcon name="lock" />
                </i>
                <i>
                  <CandidateIcon name="lock" />
                </i>
                <i>
                  <CandidateIcon name="lock" />
                </i>
              </div>
              <p className="ui-small">
                {t("candidate:dashboard.premium.test.SLOTS_HINT")}
              </p>
            </div>
          </div>
        </section>
      )}

      {showCreateProfile && (
        <Dialog
          className="--branded"
          onClose={() => setShowCreateProfile(false)}
        >
          <div>
            <div className="popup_head">
              <h2 className="popup_title">
                {t("candidate:create-profile.DIALOG_TITLE")}
              </h2>
            </div>

            <ProfileForm
              onSubmit={(values) => {
                return createProfileRequest(values).then((profile) => {
                  return navigate(`/candidate/profiles/${profile.id}`);
                });
              }}
              onCancel={() => setShowCreateProfile(false)}
            />
          </div>
        </Dialog>
      )}
    </div>
  );
};

export default CandidateDashboardPage;
